import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'

import { variantDisplayName } from '@aeris/catalog'
import type { PurchaseDetail, PurchaseSummary } from '@aeris/contracts'
import { formatMoney, toString, ZERO_MONEY } from '@aeris/money'
import {
  computePurchaseTotals,
  parseQuantity,
  parseUnitCost,
} from '@aeris/purchasing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import {
  ApiError,
  createPurchase,
  fetchProducts,
  updatePurchase,
} from '../lib/api.ts'
import { localDateInput } from '../lib/date-format.ts'
import {
  formatUnitPrice,
  parseTypedPrice,
  toWireAmount,
} from '../lib/price-format.ts'

import { PartyPicker } from './party-picker.tsx'

/**
 * The purchase editor.
 *
 * The mirror image of the sales form, and the differences are the interesting
 * part. There is no price list here: a supplier's cost is NEGOTIATED, so it is
 * typed rather than resolved — the shop is not looking it up, it is recording
 * what somebody agreed to on the telephone.
 *
 * The running total comes from `computePurchaseTotals` in `@aeris/purchasing`,
 * the same function the server stores its answer from. No second implementation
 * of the rounding lives here, so the number on screen and the number on the
 * document cannot disagree about a centavo.
 *
 * Nothing in this file does arithmetic on a `number` and nothing formats with
 * `Intl` — which takes one. ADR-0016.
 */

type DraftLine = {
  readonly key: string
  readonly variantId: string
  readonly description: string
  readonly sku: string
  readonly unit: string
  /** The wire string, at six decimals. */
  readonly unitCost: string
  readonly quantity: string
  readonly supplierCode: string | null
}

export type PurchaseFormProps = {
  /** Absent means "create". */
  document?: PurchaseDetail | undefined
  onClose: () => void
  onSaved?: ((document: PurchaseSummary) => void) | undefined
}

const toDraftLines = (document: PurchaseDetail): DraftLine[] =>
  document.lines.map((line) => ({
    key: line.id,
    variantId: line.variantId,
    description: line.description,
    sku: '',
    unit: line.unit,
    unitCost: line.unitCost,
    quantity: line.quantity,
    supplierCode: line.supplierCode,
  }))

export const PurchaseForm = ({
  document,
  onClose,
  onSaved,
}: PurchaseFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const editing = document !== undefined

  const [supplierPartyId, setSupplierPartyId] = useState(
    document?.supplierPartyId ?? ''
  )
  const [supplierDocumentNumber, setSupplierDocumentNumber] = useState(
    document?.supplierDocumentNumber ?? ''
  )
  const [expectedAt, setExpectedAt] = useState(
    // O dia local, e não o de UTC (F248): senão a previsão anda a cada salvar.
    document?.expectedAt == null ? '' : localDateInput(document.expectedAt)
  )
  const [discount, setDiscount] = useState(
    document === undefined || document.discountAmount === '0.00'
      ? ''
      : document.discountAmount.replace('.', ',')
  )
  const [freight, setFreight] = useState(
    document === undefined || document.freightAmount === '0.00'
      ? ''
      : document.freightAmount.replace('.', ',')
  )
  const [notes, setNotes] = useState(document?.notes ?? '')
  const [lines, setLines] = useState<DraftLine[]>(
    document === undefined ? [] : toDraftLines(document)
  )

  const [code, setCode] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unitCost, setUnitCost] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)

  /*
   * No default supplier.
   *
   * This used to fall back to `items[0]`, so a form left untouched booked the
   * purchase against whichever supplier sorted first alphabetically. A wrong
   * default is worse than no default here: it produces a complete, plausible
   * document that nobody was asked to confirm.
   */

  const totals = useMemo(() => {
    const outcome = computePurchaseTotals(
      lines.map((line) => ({
        quantity: parseQuantity(line.quantity) ?? ZERO_MONEY,
        unitCost: parseUnitCost(line.unitCost) ?? ZERO_MONEY,
        discountAmount: ZERO_MONEY,
      })),
      parseTypedPrice(discount) ?? ZERO_MONEY,
      parseTypedPrice(freight) ?? ZERO_MONEY
    )

    return outcome.ok ? outcome.totals : null
  }, [lines, discount, freight])

  /**
   * Finds a product by code or barcode and takes the cost that was typed.
   *
   * The cost is NOT looked up. A purchase records what the supplier is
   * charging this time, and defaulting it to the last cost would quietly turn
   * a price increase nobody noticed into a margin nobody can explain.
   */
  const addLine = useMutation({
    mutationFn: async () => {
      const term = code.trim()
      if (term.length === 0) {
        throw new ApiError(422, 'empty', 'Informe o código do produto.')
      }

      const quantityValue = parseQuantity(quantity.replace(',', '.'))
      if (quantityValue === null || quantityValue.units <= 0n) {
        throw new ApiError(
          422,
          'quantity',
          'Informe uma quantidade maior que zero.'
        )
      }

      const costValue = parseTypedPrice(unitCost)
      if (costValue === null) {
        throw new ApiError(
          422,
          'cost',
          'Informe o custo unitário desta compra.'
        )
      }

      const found = await fetchProducts({
        search: term,
        status: 'active',
        pageSize: 5,
      })

      const match = found.items
        .flatMap((product) =>
          product.variants.map((variant) => ({ product, variant }))
        )
        .find(
          ({ variant }) =>
            variant.sku.toLowerCase() === term.toLowerCase() ||
            variant.gtin === term
        )

      // An exact match on code or barcode, or nothing. A fuzzy first result
      // would silently cost the wrong product, and a wrong cost is invisible
      // until somebody wonders why a whole category stopped being profitable.
      if (match === undefined) {
        throw new ApiError(
          404,
          'variant_not_found',
          'Nenhum produto com esse código ou código de barras.'
        )
      }

      return {
        key: `${match.variant.id}-${String(Date.now())}`,
        variantId: match.variant.id,
        description: variantDisplayName(match.product.name, match.variant.name),
        sku: match.variant.sku,
        unit: match.variant.unit,
        unitCost: toWireAmount(costValue),
        quantity: toString(quantityValue),
        supplierCode: null,
      } satisfies DraftLine
    },
    onSuccess: (line) => {
      setLines((current) => [...current, line])
      setCode('')
      setQuantity('1')
      setUnitCost('')
      setLookupError(null)
    },
    onError: (cause: unknown) => {
      setLookupError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível adicionar o item.'
      )
    },
  })

  const save = useMutation({
    mutationFn: async () => {
      if (supplierPartyId.length === 0) {
        throw new ApiError(422, 'supplier', 'Escolha um fornecedor.')
      }

      const payload = {
        supplierPartyId: supplierPartyId,
        supplierDocumentNumber:
          supplierDocumentNumber.trim().length === 0
            ? null
            : supplierDocumentNumber.trim(),
        expectedAt:
          expectedAt.length === 0
            ? null
            : new Date(`${expectedAt}T23:59:59`).toISOString(),
        discountAmount: toWireAmount(parseTypedPrice(discount) ?? ZERO_MONEY),
        freightAmount: toWireAmount(parseTypedPrice(freight) ?? ZERO_MONEY),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        lines: lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          quantity: line.quantity,
          unitCost: line.unitCost,
          discountAmount: '0',
          supplierCode: line.supplierCode,
        })),
      }

      return document === undefined
        ? await createPurchase(payload)
        : await updatePurchase(document.id, {
            // The version this form was opened with. If somebody else saved in
            // the meantime the server refuses rather than overwriting them.
            version: document.version,
            ...payload,
          })
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
      onSaved?.(response.document)
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    save.mutate()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={editing ? 'Editar compra' : 'Nova compra'}
        description="O custo é digitado, não vem de tabela: é o que este fornecedor está cobrando desta vez."
        className="max-w-3xl"
      >
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Fornecedor">
              {(field) => (
                <PartyPicker
                  {...field}
                  value={supplierPartyId}
                  onChange={setSupplierPartyId}
                  role="supplier"
                />
              )}
            </Field>

            <Field
              label="Nota do fornecedor"
              hint="O número impresso na nota deles. O nosso é gerado na confirmação."
            >
              {(field) => (
                <Input
                  {...field}
                  value={supplierDocumentNumber}
                  onChange={(event) => {
                    setSupplierDocumentNumber(event.target.value)
                  }}
                />
              )}
            </Field>

            <Field
              label="Previsão de entrega"
              hint="O que o fornecedor prometeu, quando prometeu alguma coisa."
            >
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={expectedAt}
                  onChange={(event) => {
                    setExpectedAt(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>

          <div className="flex flex-wrap items-end gap-2 rounded-(--radius-md) border border-(--border-subtle) p-3">
            <Field
              label="Código ou código de barras"
              className="min-w-56 flex-1"
            >
              {(field) => (
                <Input
                  {...field}
                  value={code}
                  autoComplete="off"
                  onChange={(event) => {
                    setCode(event.target.value)
                  }}
                  onKeyDown={(event) => {
                    // A scanner ends its read with Enter. Submitting the whole
                    // form on that key would save a half-built purchase on
                    // every scan.
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      addLine.mutate()
                    }
                  }}
                />
              )}
            </Field>

            <Field label="Quantidade" className="w-28">
              {(field) => (
                <Input
                  {...field}
                  value={quantity}
                  inputMode="decimal"
                  onChange={(event) => {
                    setQuantity(event.target.value)
                  }}
                />
              )}
            </Field>

            <Field label="Custo unitário" className="w-36">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={unitCost}
                  onValueChange={setUnitCost}
                />
              )}
            </Field>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={addLine.isPending}
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                addLine.mutate()
              }}
            >
              Adicionar item
            </Button>
          </div>

          {lookupError === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {lookupError}
            </p>
          )}

          {lines.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum item ainda.
            </p>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Produto</TableHeaderCell>
                    <TableHeaderCell>Un.</TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Qtd.
                    </TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Custo
                    </TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Total
                    </TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      <span className="sr-only">Ações</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.key}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell>{line.unit}</TableCell>
                      <TableCell className="text-right font-(--font-mono)">
                        {line.quantity.replace(/\.?0+$/, '')}
                      </TableCell>
                      <TableCell className="text-right font-(--font-mono)">
                        {formatUnitPrice(line.unitCost)}
                      </TableCell>
                      <TableCell className="text-right font-(--font-mono)">
                        {formatMoney(
                          totals?.lines[index]?.lineTotal ?? ZERO_MONEY
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remover ${line.description}`}
                          onClick={() => {
                            setLines((current) =>
                              current.filter((entry) => entry.key !== line.key)
                            )
                          }}
                          leadingIcon={
                            <Trash2
                              aria-hidden="true"
                              className="h-3.5 w-3.5"
                            />
                          }
                        >
                          Remover
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Field
                label="Desconto"
                hint="Distribuído entre os itens, para que a soma bata com o total."
                className="w-44"
              >
                {(field) => (
                  <MoneyInput
                    {...field}
                    value={discount}
                    onValueChange={setDiscount}
                  />
                )}
              </Field>

              {/* On the header, not spread into the unit costs: dividing
                  freight by value, by weight or by quantity is a real
                  accounting choice with different right answers, and ADR-0020
                  leaves it open rather than guessing. */}
              <Field
                label="Frete e outras despesas"
                hint="Somado ao total, sem entrar no custo unitário."
                className="w-44"
              >
                {(field) => (
                  <MoneyInput
                    {...field}
                    value={freight}
                    onValueChange={setFreight}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-col items-end gap-1">
              <span className="text-(length:--text-xs) text-(--text-muted)">
                Total da compra
              </span>
              <output
                aria-live="polite"
                className="font-(--font-mono) text-(length:--text-xl) text-(--text-primary)"
              >
                {formatMoney(totals?.total ?? ZERO_MONEY)}
              </output>
              {totals === null ? (
                <Badge tone="danger">Desconto maior que o total</Badge>
              ) : null}
            </div>
          </div>

          <Field label="Observações">
            {(field) => (
              <Input
                {...field}
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value)
                }}
              />
            )}
          </Field>

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={save.isPending || totals === null}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
